import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AppSerialized } from '@playq/octopus2-apps';
import { PermsSerialized, Perms, AccessLevel, PermModule } from '@playq/octopus2-auth';

import { getUrlAppName } from '/helpers';
import { systemRehydrate } from '/store/systemActions';

import { login } from '../auth/actions';

import { appSelectors, baseSelectors } from './selectors';

export interface IAppState {
  app?: AppSerialized;
  perms?: PermsSerialized;
  pending: boolean;
  error?: string;
}

export interface IAppSelectPayload {
  app: AppSerialized;
}

export interface IModulePerms {
  canRead: boolean;
  canWrite: boolean;
  canWriteLive: boolean;
}

export type IPermissionsAccess = Record<PermModule, AccessLevel>;

const initialState: IAppState = {
  pending: false,
};

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    updateApp: (state, { payload }: PayloadAction<AppSerialized>) => {
      state.app = payload;
    },
    updatePerms: (state, { payload }: PayloadAction<Perms>) => {
      state.perms = payload.serialize();
    },
    resetApp: () => initialState,
    failure: (state, { payload }: PayloadAction<string>) => {
      state.error = payload;
      state.pending = false;
    },
    updateAccess: (state, { payload }: PayloadAction<{ app: AppSerialized; perms: PermsSerialized }>) => {
      const { app, perms } = payload;
      state.app = app;
      state.perms = perms;
      state.error = undefined;
      state.pending = false;
    },
    startAppSelecting: (state, { payload }: PayloadAction<IAppSelectPayload>) => {
      state.app = payload.app;
      state.pending = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        const isApp = !!getUrlAppName(window.location.pathname);
        state.pending = isApp;
      })
      .addCase(systemRehydrate, (state) => {
        const isApp = !!getUrlAppName(window.location.pathname);
        state.pending = isApp;
      });
  },
});

export const appToolkit = Object.freeze({
  initialState,
  actions: appSlice.actions,
  selectors: {
    ...baseSelectors,
    ...appSelectors,
  },
});

export const appReducer = appSlice.reducer;
